import React, { useCallback, useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { UserCheck, Ban, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtRM } from "@/lib/format";
import { useBranch } from "@/context/BranchContext";

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function CollectorWorkbench() {
  const { branch } = useBranch();
  const [data, setData] = useState({ collectors: [], grandTotals: {} });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/bad-debt/collectors")
      .then((r) => {
        setData(r.data);
        setActive((prev) => prev || (r.data.collectors[0]?.collectorName ?? null));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, branch]);

  const g = data.grandTotals || {};
  const current = (data.collectors || []).find((c) => c.collectorName === active);

  return (
    <Layout>
      <PageHeader title="Collector Workbench" subtitle="Bad-debt accounts each collector is chasing" />

      <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Collectors</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-slate-900" data-testid="wb-collector-count">{g.collectors || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Accounts Chasing</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-blue-700" data-testid="wb-account-count">{g.count || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Total Outstanding</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-red-600" data-testid="wb-total-outstanding">{fmtRM(g.totalOutstanding || 0)}</div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-slate-400 animate-pulse text-sm">Loading…</div>
      ) : (data.collectors || []).length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2" data-testid="wb-empty">
          <UserCheck className="h-8 w-8 text-slate-300" /> No accounts are being chased by a collector yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Collector list */}
          <div className="lg:col-span-1 space-y-2" data-testid="collector-list">
            {data.collectors.map((c) => (
              <button
                key={c.collectorName}
                data-testid={`collector-tab-${c.collectorName}`}
                onClick={() => setActive(c.collectorName)}
                className={`w-full text-left border rounded-lg p-3 transition-colors ${active === c.collectorName ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-slate-900">
                  <UserCheck className="h-4 w-4 text-blue-600" /> {c.collectorName}
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-slate-500">{c.count} account{c.count !== 1 ? "s" : ""}</span>
                  <span className="font-mono-fig font-semibold text-red-600">{fmtRM(c.totalOutstanding)}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Selected collector's accounts */}
          <div className="lg:col-span-3">
            {current && (
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="collector-accounts">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-blue-600" /> {current.collectorName}
                  </h3>
                  <div className="text-sm text-slate-500">
                    {current.count} accounts · <span className="font-mono-fig text-red-600 font-semibold">{fmtRM(current.totalOutstanding)}</span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                      <th className="px-4 py-3 font-semibold">Loan</th>
                      <th className="px-4 py-3 font-semibold">Customer</th>
                      <th className="px-4 py-3 font-semibold">Branch</th>
                      <th className="px-4 py-3 font-semibold">Phone</th>
                      <th className="px-4 py-3 font-semibold text-right">Outstanding</th>
                      <th className="px-4 py-3 font-semibold">Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.loans.map((l) => (
                      <tr key={l.code} data-testid="collector-account-row" className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-3"><Link to={`/loans/${l.code}`} className="font-mono-fig text-slate-900 hover:underline">{l.code}</Link></td>
                        <td className="px-4 py-3 text-slate-700">{l.customerName}</td>
                        <td className="px-4 py-3 text-slate-600">{l.branch}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {l.phone ? (
                            <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="h-3 w-3 text-slate-400" />{l.phone}</a>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono-fig text-red-600">{fmtRM(l.outstanding)}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtDate(l.badDebtDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
